import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { Dialog } from '@/_shared/components/Dialog';
import { getEmployeesStatsCall } from '@/people/state';
import { getSurveysCampaignsCall } from '@/surveys/state';
import { getSurveyCampaignByIdResponsesStatsCall } from '@/surveys/state/api';
import { Steps } from '../../state/constats';
import { StepChooser } from './StepChooser';
import { StepLastConfirm } from './StepLastConfirm';
import { StepListEmployees } from './StepListEmployees';
import { StepRecapAll } from './StepRecapAll';
import { StepRecapDepartments } from './StepRecapDepartments';
import { StepTemplates } from './StepTemplates';

/**
 * Navigation flow managed by SendSurvey:
 *
 * ```
 * [STEP_TEMPLATES] (preSelection only)
 *      ↓
 * [STEP_CHOOSER]
 *   ├─ "Invia tutti"         → [STEP_RECIPIENTS] (inviteAll=true)
 *   ├─ "Per dipartimento"    → [STEP_DEPARTMENTS]
 *   │       ├─ "Continua"    → [STEP_RECIPIENTS]
 *   │       └─ "Vedi persone"→ [STEP_EMPLOYEES] (filtered by dept)
 *   │               └─ "Continua" → [STEP_RECIPIENTS]
 *   └─ "Scelta individuale"  → [STEP_EMPLOYEES] (all employees)
 *           └─ "Continua"   → [STEP_RECIPIENTS]
 *
 * [STEP_RECIPIENTS] → "Invia" → [STEP_CONFIRM]
 * ```
 *
 * State management for navigation history:
 * - `previousStep`: tracks what step came BEFORE `STEP_EMPLOYEES` (forward navigation only).
 *   Used to decide whether `StepListEmployees` should filter by departments and where its
 *   back button should go. NOT updated when navigating backward (STEP_RECIPIENTS → STEP_EMPLOYEES).
 *
 * - `recipientsBackStep`: tracks where the back button in `StepRecapAll` (STEP_RECIPIENTS)
 *   should navigate. Always set to the current step whenever navigating to STEP_RECIPIENTS.
 *   Kept separate from `previousStep` to avoid corruption when the user goes back and re-enters
 *   the flow (e.g. departments → continua → recap → back → departments → vedi persone → recap → back).
 */
type Props = {
  preSelection?: boolean;
  isOpen: boolean;
  closeDialog: () => void;
  currentTemplateId?: string;
  currentTemplateName?: string;
  currentVersionId?: number;
  currentCampaignId?: string;
  isExistingCampaign?: boolean;
  isVisible?: boolean;
  setTrigger?: (value: boolean) => void;
  customRedirectOnClose?: string;
};

/**
 * Orchestrates the multi-step "Send Survey" dialog.
 * See the `Props` type comment above for the full navigation flow and state management strategy.
 */
export const SendSurvey: FC<Props> = ({
  preSelection = false,
  isOpen,
  closeDialog,
  currentTemplateId,
  currentTemplateName,
  currentVersionId,
  currentCampaignId,
  isExistingCampaign = false,
  isVisible,
  setTrigger,
  customRedirectOnClose = undefined,
}) => {
  const t = useTranslations('surveys.sendSurvey');

  const [step, setStep] = useState<Steps>(Steps.STEP_CHOOSER);
  const [previousStep, setPreviousStep] = useState<Steps>(Steps.STEP_CHOOSER);
  const [recipientsBackStep, setRecipientsBackStep] = useState<Steps>(Steps.STEP_CHOOSER);
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [inviteAll, setInviteAll] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    !preSelection ? (currentTemplateName ?? '') : '',
  );

  const { completed } = getEmployeesStatsCall.hook();

  const { execute: executeGetSurveysCampaigns } = getSurveysCampaignsCall.hook();
  const { response: responseGetSurveyCampaignByIdResponsesStats } =
    getSurveyCampaignByIdResponsesStatsCall.hook();

  const totalToInvite = useMemo(() => {
    return Math.max(
      0,
      completed - (responseGetSurveyCampaignByIdResponsesStats?.totalInvitation ?? 0),
    );
  }, [responseGetSurveyCampaignByIdResponsesStats, completed]);

  /**
   * Centralized step navigation handler.
   *
   * Rules:
   * - Navigating to STEP_CHOOSER resets all selection state.
   * - `previousStep` is updated only on FORWARD navigation to STEP_EMPLOYEES
   *   (i.e. not when the back button in STEP_RECIPIENTS sends the user back to STEP_EMPLOYEES),
   *   so that `filterDepartments` and `backStep` in StepListEmployees remain correct.
   * - `recipientsBackStep` is always updated when navigating to STEP_RECIPIENTS,
   *   so the back button in StepRecapAll always returns to the right step.
   */
  const handleSetStep = useCallback(
    (newStep: Steps) => {
      if (newStep === Steps.STEP_CHOOSER) {
        setSelectedUsersIds([]);
        setSelectedDepartments([]);
        setInviteAll(false);
      }
      // Track origin of STEP_EMPLOYEES only on forward navigation (not when going back from STEP_RECIPIENTS)
      if (newStep === Steps.STEP_EMPLOYEES && step !== Steps.STEP_RECIPIENTS) {
        setPreviousStep(step);
      }
      // Track back destination for STEP_RECIPIENTS (always the current step)
      if (newStep === Steps.STEP_RECIPIENTS) {
        setRecipientsBackStep(step);
      }
      setStep(newStep);
    },
    [step],
  );

  const onCustomClose = useCallback(
    (openTrigger: boolean = false) => {
      if (isCompleted) {
        executeGetSurveysCampaigns({});
        if (customRedirectOnClose !== undefined) {
          window.location.href = `${customRedirectOnClose}?tab=assessment`;
        }
      }
      if (setTrigger && openTrigger) {
        setTrigger(true);
      }
      closeDialog();
    },
    [isCompleted, setTrigger, closeDialog, executeGetSurveysCampaigns, customRedirectOnClose],
  );

  useEffect(() => {
    if (!isOpen) {
      if (preSelection) {
        setStep(Steps.STEP_TEMPLATES);
        setSelectedTemplate('');
      } else {
        setStep(Steps.STEP_CHOOSER);
        setSelectedTemplate(currentTemplateName ?? '');
      }
      setSelectedUsersIds([]);
      setInviteAll(false);
    }
  }, [isOpen, preSelection, currentTemplateName]);

  return (
    <Dialog
      maxWidth='1100px'
      name='sendSurvey'
      open={isOpen}
      title={undefined}
      onClose={() => onCustomClose(false)}
      closeButton
    >
      <Box width='100%'>
        <Box id='dialog-send-survey-content-title' display='flex' flexDirection='column'>
          <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
            {t('title', { templateName: step !== Steps.STEP_TEMPLATES ? selectedTemplate : '' })}
          </Typography>
        </Box>
        {step === Steps.STEP_TEMPLATES && (
          <StepTemplates
            setSelectedTemplate={setSelectedTemplate}
            selectedTemplate={selectedTemplate}
            setStep={handleSetStep}
          />
        )}
        {step === Steps.STEP_CHOOSER && (
          <StepChooser
            totalToInvite={totalToInvite}
            setInviteAll={setInviteAll}
            setStep={handleSetStep}
            selectedTemplate={selectedTemplate}
            preSelection={preSelection}
          />
        )}
        {step === Steps.STEP_RECIPIENTS && (
          <StepRecapAll
            totalToInvite={totalToInvite}
            selectedUsersIds={selectedUsersIds}
            setStep={handleSetStep}
            previousStep={recipientsBackStep}
            currentCampaignId={currentCampaignId}
            currentTemplateId={currentTemplateId}
            currentVersionId={currentVersionId}
            isExistingCampaign={isExistingCampaign}
            setIsCompleted={setIsCompleted}
            isVisible={isVisible}
            inviteAll={inviteAll}
            selectedTemplate={selectedTemplate}
            setSelectedDepartments={setSelectedDepartments}
          />
        )}
        {step === Steps.STEP_DEPARTMENTS && (
          <StepRecapDepartments
            currentCampaignId={currentCampaignId}
            currentTemplateId={currentTemplateId}
            currentVersionId={currentVersionId}
            selectedDepartments={selectedDepartments}
            setSelectedDepartments={setSelectedDepartments}
            selectedUsersIds={selectedUsersIds}
            setSelectedUsersIds={setSelectedUsersIds}
            setStep={handleSetStep}
            selectedTemplate={selectedTemplate}
          />
        )}
        {step === Steps.STEP_EMPLOYEES && (
          <StepListEmployees
            currentCampaignId={currentCampaignId}
            currentTemplateId={currentTemplateId}
            currentVersionId={currentVersionId}
            selectedUsersIds={selectedUsersIds}
            setSelectedUsersIds={setSelectedUsersIds}
            setStep={handleSetStep}
            selectedTemplate={selectedTemplate}
            filterDepartments={
              previousStep === Steps.STEP_DEPARTMENTS ? selectedDepartments : undefined
            }
            backStep={
              previousStep === Steps.STEP_DEPARTMENTS ? Steps.STEP_DEPARTMENTS : Steps.STEP_CHOOSER
            }
          />
        )}
        {step === Steps.STEP_CONFIRM && (
          <StepLastConfirm
            inviteAll={inviteAll}
            totalToInvite={totalToInvite}
            selectedUsersIds={selectedUsersIds}
            onCustomClose={onCustomClose}
            selectedTemplate={selectedTemplate}
            isExistingCampaign={isExistingCampaign}
            currentCampaignId={currentCampaignId}
          />
        )}
      </Box>
    </Dialog>
  );
};
